import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import Sidebar from '../Dashboard/Sidebar';
import Loader from '../layouts/Loader';
import MetaData from '../layouts/MetaData';
import { getAllStaff, deleteStaff } from '../../actions/staffActions';
import { clearStaffDeleted, clearStaffError } from '../../slices/staffSlice';
import formatDate from '../../utils/formatDate';
import DeleteConfirmModal from '../layouts/DeleteConfirmModal';
import { downloadPaySlipPDF, numberToWords } from '../../utils/paySlipPdf';
import { downloadExperienceLetterPDF } from '../../utils/experienceLetterPdf';

const getFileUrl = (item) => {
  if (!item) return '';
  let url = typeof item === 'string' ? item : (item.file || item.url || '');
  if (!url || typeof url !== 'string') return '';
  if (url.includes('localhost:8000') && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    url = url.replace(/https?:\/\/localhost:8000/, '');
  }
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const isDev = (window.location.port === '3000' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));
  const backend = isDev ? 'http://localhost:8000' : '';
  return `${backend}${url.startsWith('/') ? '' : '/'}${url}`;
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function StaffList() {
  const dispatch = useDispatch();
  const { loading, staffList, isStaffDeleted, error } = useSelector(state => state.staffState);
  const { user } = useSelector(state => state.authState);
  const isAdmin = user?.role === 'admin' || user?.role === 'master';
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(clearStaffDeleted());
    dispatch(clearStaffError());
    dispatch(getAllStaff());
    return () => {
      dispatch(clearStaffDeleted());
      dispatch(clearStaffError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast(error, { type: 'error' });
      dispatch(clearStaffError());
    }
    if (isStaffDeleted) {
      toast('Staff removed', { type: 'success' });
      dispatch(clearStaffDeleted());
      dispatch(getAllStaff());
    }
  }, [error, isStaffDeleted, dispatch]);

  const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: '' });

  const handleDelete = (id, name) => {
    setDeleteModal({ show: true, id, name });
  };

  const confirmDelete = () => {
    dispatch(deleteStaff(deleteModal.id));
    setDeleteModal({ show: false, id: null, name: '' });
  };

  // --------------------------------------------------------------------------
  // PAY SLIP MODAL STATE & HANDLERS
  // --------------------------------------------------------------------------
  const [paySlipModal, setPaySlipModal] = useState({
    show: false,
    staff: null,
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    loading: false,
    baseSalary: 0,
    allowance: 0,
    deduction: 0,
    otherDeductions: 0,
    summary: {
      present: 0,
      absent: 0,
      halfDay: 0,
      holiday: 0,
      payableDays: 30,
    },
    notes: '',
  });

  const fetchStaffMonthlyAttendance = async (staffId, y, m, salaryVal) => {
    try {
      setPaySlipModal(prev => ({ ...prev, loading: true }));
      const { data } = await axios.get(`/api/v1/admin/attendance/staff/${staffId}?year=${y}&month=${m}`);
      if (data && data.success) {
        const sum = data.summary || { present: 0, absent: 0, halfDay: 0, holiday: 0 };
        const salary = Number(salaryVal !== undefined ? salaryVal : paySlipModal.baseSalary || 0);
        const perDay = salary > 0 ? (salary / 30) : 0;
        const autoDeduction = Math.round((sum.absent * perDay) + (sum.halfDay * perDay * 0.5));
        const payableDays = Math.max(0, 30 - sum.absent - (sum.halfDay * 0.5));

        setPaySlipModal(prev => ({
          ...prev,
          loading: false,
          deduction: autoDeduction,
          summary: {
            ...sum,
            payableDays,
          }
        }));
      } else {
        setPaySlipModal(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error('Error fetching monthly attendance:', err);
      setPaySlipModal(prev => ({ ...prev, loading: false }));
    }
  };

  const openPaySlipModal = (staff) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const salary = Number(staff.monthlySalary || 0);

    setPaySlipModal({
      show: true,
      staff,
      year: currentYear,
      month: currentMonth,
      loading: true,
      baseSalary: salary,
      allowance: 0,
      deduction: 0,
      otherDeductions: 0,
      summary: {
        present: 0,
        absent: 0,
        halfDay: 0,
        holiday: 0,
        payableDays: 30,
      },
      notes: '',
    });

    fetchStaffMonthlyAttendance(staff._id || staff.id, currentYear, currentMonth, salary);
  };

  const handlePaySlipMonthYearChange = (newYear, newMonth) => {
    setPaySlipModal(prev => ({ ...prev, year: newYear, month: newMonth }));
    if (paySlipModal.staff) {
      fetchStaffMonthlyAttendance(
        paySlipModal.staff._id || paySlipModal.staff.id,
        newYear,
        newMonth,
        paySlipModal.baseSalary
      );
    }
  };

  const handleBaseSalaryChange = (newSalary) => {
    const salary = Number(newSalary || 0);
    const perDay = salary > 0 ? (salary / 30) : 0;
    const autoDeduction = Math.round((paySlipModal.summary.absent * perDay) + (paySlipModal.summary.halfDay * perDay * 0.5));
    setPaySlipModal(prev => ({
      ...prev,
      baseSalary: newSalary,
      deduction: autoDeduction
    }));
  };

  const handleDownloadPaySlip = () => {
    if (!paySlipModal.staff) return;
    const totalEarnings = Number(paySlipModal.baseSalary || 0) + Number(paySlipModal.allowance || 0);
    const totalDeductions = Number(paySlipModal.deduction || 0) + Number(paySlipModal.otherDeductions || 0);
    const netSalary = Math.max(0, totalEarnings - totalDeductions);

    downloadPaySlipPDF(
      paySlipModal.staff,
      {
        summary: paySlipModal.summary,
        baseSalary: Number(paySlipModal.baseSalary || 0),
        allowance: Number(paySlipModal.allowance || 0),
        deduction: Number(paySlipModal.deduction || 0),
        otherDeductions: Number(paySlipModal.otherDeductions || 0),
        payableDays: paySlipModal.summary.payableDays,
        netSalary,
        notes: paySlipModal.notes
      },
      paySlipModal.year,
      paySlipModal.month
    );
  };

  const handleSharePaySlipWhatsApp = () => {
    if (!paySlipModal.staff) return;
    const staff = paySlipModal.staff;
    const cleanPhone = (staff.phone || '').replace(/[^0-9]/g, '');
    const phoneWithCode = cleanPhone.startsWith('91') ? cleanPhone : (cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone);
    const monthName = MONTH_NAMES[paySlipModal.month - 1] || 'Current Month';
    const totalEarnings = Number(paySlipModal.baseSalary || 0) + Number(paySlipModal.allowance || 0);
    const totalDeductions = Number(paySlipModal.deduction || 0) + Number(paySlipModal.otherDeductions || 0);
    const netSalary = Math.max(0, totalEarnings - totalDeductions);

    const lines = [
      `*GREEN NATUROPATHY - SALARY PAY SLIP*`,
      `Dear ${staff.name},`,
      `Your official salary slip for *${monthName} ${paySlipModal.year}* has been processed.`,
      ``,
      `*Attendance Summary (30-day base):*`,
      `• Present Days: ${paySlipModal.summary.present || 0}`,
      `• Holidays / Off: ${paySlipModal.summary.holiday || 0}`,
      `• Half Days: ${paySlipModal.summary.halfDay || 0}`,
      `• Leaves / Absent: ${paySlipModal.summary.absent || 0}`,
      `• Paid Days: ${paySlipModal.summary.payableDays || 30}`,
      ``,
      `*Salary Details:*`,
      `• Basic Monthly Salary: Rs. ${Number(paySlipModal.baseSalary || 0).toLocaleString('en-IN')}`,
      Number(paySlipModal.allowance || 0) > 0 ? `• Allowances: Rs. ${Number(paySlipModal.allowance).toLocaleString('en-IN')}` : null,
      Number(paySlipModal.deduction || 0) > 0 ? `• Leave Deduction: Rs. ${Number(paySlipModal.deduction).toLocaleString('en-IN')}` : null,
      Number(paySlipModal.otherDeductions || 0) > 0 ? `• Other Deductions: Rs. ${Number(paySlipModal.otherDeductions).toLocaleString('en-IN')}` : null,
      `*Net Payable Salary: Rs. ${netSalary.toLocaleString('en-IN')}*`,
      `(${numberToWords(netSalary)})`,
      ``,
      `Please contact hospital administration if you need further details or an authorized hard copy.`
    ].filter(Boolean);

    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneWithCode}&text=${encodeURIComponent(lines.join('\n'))}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  // --------------------------------------------------------------------------
  // EXPERIENCE LETTER MODAL STATE & HANDLERS
  // --------------------------------------------------------------------------
  const [expModal, setExpModal] = useState({
    show: false,
    staff: null,
    issueDate: new Date().toISOString().slice(0, 10),
    joiningDate: '',
    isCurrentlyWorking: true,
    relievingDate: new Date().toISOString().slice(0, 10),
    designation: '',
    refNo: '',
    remarks: '',
  });

  const openExpLetterModal = (staff) => {
    const isWorking = staff.status === 'Active';
    let jDate = '';
    if (staff.joiningDate) {
      try {
        jDate = new Date(staff.joiningDate).toISOString().slice(0, 10);
      } catch (e) {
        jDate = '';
      }
    }
    const currentYear = new Date().getFullYear();
    const cleanStaffId = (staff.staffId || '001').replace(/[^a-zA-Z0-9]/g, '');

    setExpModal({
      show: true,
      staff,
      issueDate: new Date().toISOString().slice(0, 10),
      joiningDate: jDate,
      isCurrentlyWorking: isWorking,
      relievingDate: new Date().toISOString().slice(0, 10),
      designation: staff.designation || 'Staff Member',
      refNo: `GN/EXP/${currentYear}/${cleanStaffId}`,
      remarks: 'During his/her period of service with our hospital, we found him/her to be sincere, dedicated, hardworking, and punctual in performing assigned responsibilities. His/Her professional knowledge, conduct, and interpersonal relationships with patients, doctors, and team members were exemplary.',
    });
  };

  const handleDownloadExpLetter = () => {
    if (!expModal.staff) return;
    downloadExperienceLetterPDF({
      staff: {
        ...expModal.staff,
        joiningDate: expModal.joiningDate || expModal.staff.joiningDate
      },
      issueDate: expModal.issueDate,
      relievingDate: expModal.relievingDate,
      isCurrentlyWorking: expModal.isCurrentlyWorking,
      designation: expModal.designation,
      remarks: expModal.remarks,
      refNo: expModal.refNo
    });
  };

  const handleShareExpWhatsApp = () => {
    if (!expModal.staff) return;
    const staff = expModal.staff;
    const cleanPhone = (staff.phone || '').replace(/[^0-9]/g, '');
    const phoneWithCode = cleanPhone.startsWith('91') ? cleanPhone : (cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone);
    const tenureDesc = expModal.isCurrentlyWorking
      ? `from ${formatDate(expModal.joiningDate || staff.joiningDate)} till date`
      : `from ${formatDate(expModal.joiningDate || staff.joiningDate)} to ${formatDate(expModal.relievingDate)}`;

    const lines = [
      `*GREEN NATUROPATHY - EXPERIENCE CERTIFICATE*`,
      `Dear ${staff.name},`,
      ``,
      `Your official Experience Certificate / Relieving Document has been generated.`,
      ``,
      `*Certificate Summary:*`,
      `• Reference No: ${expModal.refNo}`,
      `• Designation: ${expModal.designation}`,
      `• Period of Service: ${tenureDesc}`,
      `• Issue Date: ${formatDate(expModal.issueDate)}`,
      ``,
      `We sincerely appreciate your valuable service, dedication, and contributions to Green Naturopathy. We wish you grand success in all your future professional endeavors!`,
      ``,
      `_Issued by Green Naturopathy Management_`
    ];

    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneWithCode}&text=${encodeURIComponent(lines.join('\n'))}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const filteredStaff = (staffList || []).filter(s =>
    `${s.name} ${s.staffId} ${s.phone}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">
        <MetaData title="Staff" />

        {loading ? (
          <Loader />
        ) : (
          <Fragment>
            {/* Page header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
              <div>
                <h1 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>
                  Staff
                  <span
                    className="ms-2"
                    style={{
                      background: 'rgba(13,148,136,0.1)',
                      color: 'var(--primary)',
                      border: '1px solid rgba(13,148,136,0.25)',
                      padding: '0.2em 0.6em',
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {staffList?.length || 0}
                  </span>
                </h1>
                <p className="mb-0 mt-1" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  Manage hospital staff, payroll slips, attendance & experience letters
                </p>
              </div>
              <div className="d-flex gap-2">
                <input
                  className="form-control"
                  style={{ maxWidth: 220 }}
                  placeholder="Search name, ID, phone…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {isAdmin && (
                  <Link to="/admin/staff/new" className="btn btn-primary shadow-sm" style={{ padding: '0.5rem 1.25rem', borderRadius: '8px' }}>
                    <i className="fa fa-user-plus me-2"></i>Add Staff
                  </Link>
                )}
              </div>
            </div>

            {filteredStaff.length > 0 ? (
              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0 align-middle" style={{ fontSize: '0.875rem' }}>
                      <thead>
                        <tr>
                          <th>Staff ID</th>
                          <th>Name</th>
                          <th>Designation</th>
                          <th>Phone / Email</th>
                          <th>Joining Date</th>
                          <th>Monthly Salary</th>
                          <th>Status</th>
                          <th className="text-end" style={{ minWidth: 280 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStaff.map(s => (
                          <Fragment key={s._id}>
                            <tr>
                              <td style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{s.staffId}</td>
                              <td className="fw-semibold text-dark">{s.name}</td>
                              <td>{s.designation || '—'}</td>
                              <td>
                                <div>{s.phone}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.email}</div>
                              </td>
                              <td>{formatDate(s.joiningDate || s.createdAt)}</td>
                              <td className="fw-semibold">
                                {s.monthlySalary ? `₹${Number(s.monthlySalary).toLocaleString('en-IN')}` : '—'}
                              </td>
                              <td>
                                <span
                                  style={{
                                    background: s.status === 'Active'
                                      ? 'rgba(16,185,129,0.12)' : 'rgba(100,116,139,0.12)',
                                    color: s.status === 'Active' ? '#065f46' : '#475569',
                                    border: s.status === 'Active'
                                      ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(100,116,139,0.25)',
                                    padding: '0.28em 0.65em',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    display: 'inline-block',
                                  }}
                                >
                                  {s.status}
                                </span>
                              </td>
                              <td className="text-end">
                                <div className="d-inline-flex align-items-center gap-1 flex-wrap justify-content-end">
                                  {/* Pay Slip Generator */}
                                  <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{
                                      background: 'rgba(16,185,129,0.09)',
                                      color: '#059669',
                                      border: '1px solid rgba(16,185,129,0.25)',
                                      borderRadius: '6px',
                                      fontWeight: 500,
                                    }}
                                    onClick={() => openPaySlipModal(s)}
                                    title="Generate Salary Pay Slip"
                                  >
                                    <i className="fa fa-file-text-o me-1"></i>Payslip
                                  </button>

                                  {/* Experience Certificate */}
                                  <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{
                                      background: 'rgba(124,58,237,0.09)',
                                      color: '#7c3aed',
                                      border: '1px solid rgba(124,58,237,0.25)',
                                      borderRadius: '6px',
                                      fontWeight: 500,
                                    }}
                                    onClick={() => openExpLetterModal(s)}
                                    title="Generate Official Experience Certificate"
                                  >
                                    <i className="fa fa-certificate me-1"></i>Exp Letter
                                  </button>

                                  {/* Expand / Details */}
                                  <button
                                    className="btn btn-sm"
                                    style={{
                                      background: 'rgba(13,148,136,0.08)',
                                      color: 'var(--primary)',
                                      border: '1px solid rgba(13,148,136,0.2)',
                                      borderRadius: '6px',
                                      fontWeight: 500,
                                    }}
                                    onClick={() => setExpanded(expanded === s._id ? null : s._id)}
                                  >
                                    {expanded === s._id ? (
                                      <><i className="fa fa-chevron-up me-1"></i>Hide</>
                                    ) : (
                                      <><i className="fa fa-chevron-down me-1"></i>View</>
                                    )}
                                  </button>

                                  {/* Edit Staff */}
                                  {isAdmin && (
                                    <Link
                                      to={`/admin/staff/${s._id || s.id}/edit`}
                                      className="btn btn-sm"
                                      style={{
                                        background: 'rgba(59,130,246,0.08)',
                                        color: '#3b82f6',
                                        border: '1px solid rgba(59,130,246,0.2)',
                                        borderRadius: '6px',
                                        fontWeight: 500,
                                      }}
                                    >
                                      <i className="fa fa-pencil me-1"></i>Edit
                                    </Link>
                                  )}

                                  {/* Delete Staff */}
                                  {isAdmin && (
                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        background: 'rgba(239,68,68,0.08)',
                                        color: 'var(--danger)',
                                        border: '1px solid rgba(239,68,68,0.2)',
                                        borderRadius: '6px',
                                        fontWeight: 500,
                                      }}
                                      onClick={() => handleDelete(s._id || s.id, s.name)}
                                    >
                                      <i className="fa fa-trash me-1"></i>Delete
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {/* Expandable detail row */}
                            {expanded === s._id && (
                              <tr>
                                <td
                                  colSpan={8}
                                  style={{
                                    background: 'rgba(13,148,136,0.04)',
                                    borderTop: '1px dashed rgba(13,148,136,0.2)',
                                  }}
                                >
                                  <div className="row g-3 py-3 px-3">
                                    <div className="col-md-5">
                                      <p className="mb-1 small">
                                        <strong>Address:</strong> {s.address || '—'}
                                      </p>
                                      <p className="mb-1 small">
                                        <strong>Parent / Alternate Phone:</strong> {s.parentPhone || '—'}
                                      </p>
                                      <p className="mb-1 small">
                                        <strong>Remarks:</strong> {s.remarks || '—'}
                                      </p>
                                      <p className="mb-1 small">
                                        <strong>Date Joined:</strong>{' '}
                                        {formatDate(s.joiningDate || s.createdAt)}
                                      </p>
                                    </div>
                                    <div className="col-md-4">
                                      <p className="mb-2 small"><strong>ID Proof:</strong></p>
                                      {s.idProof && s.idProof.length > 0 ? (
                                        s.idProof.map((f, i) => {
                                          const fileUrl = getFileUrl(f);
                                          if (!fileUrl) return null;
                                          return (
                                            <a
                                              key={i}
                                              href={fileUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="btn btn-sm me-2 mb-2"
                                              style={{
                                                background: 'rgba(100,116,139,0.08)',
                                                color: '#475569',
                                                border: '1px solid rgba(100,116,139,0.2)',
                                                borderRadius: '6px',
                                                fontSize: '0.78rem',
                                              }}
                                            >
                                              {f?.fileType === 'pdf' ? '📄' : '🖼️'} Proof {i + 1}
                                            </a>
                                          );
                                        })
                                      ) : (
                                        <span className="text-muted small d-block mb-2">No ID proof uploaded</span>
                                      )}
                                      <p className="mb-2 small mt-2"><strong>Resume:</strong></p>
                                      {(() => {
                                        const resumeObj = s.resume && !Array.isArray(s.resume)
                                          ? s.resume
                                          : (Array.isArray(s.resume) && s.resume.length > 0 ? s.resume[0] : null);
                                        const resumeUrl = getFileUrl(resumeObj);
                                        return resumeUrl ? (
                                          <a
                                            href={resumeUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm mb-2"
                                            style={{
                                              background: 'rgba(13,148,136,0.08)',
                                              color: 'var(--primary)',
                                              border: '1px solid rgba(13,148,136,0.2)',
                                              borderRadius: '6px',
                                              fontSize: '0.78rem',
                                            }}
                                          >
                                            📄 View Resume
                                          </a>
                                        ) : (
                                          <span className="text-muted small d-block">No resume uploaded</span>
                                        );
                                      })()}
                                    </div>

                                    {/* Quick Document generation shortcuts */}
                                    <div className="col-md-3 border-start ps-md-3">
                                      <p className="mb-2 small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                        Quick Documents
                                      </p>
                                      <div className="d-flex flex-column gap-2">
                                        <button
                                          type="button"
                                          className="btn btn-sm text-start"
                                          style={{
                                            background: '#fff',
                                            border: '1px solid rgba(16,185,129,0.3)',
                                            color: '#047857',
                                            borderRadius: '6px'
                                          }}
                                          onClick={() => openPaySlipModal(s)}
                                        >
                                          <i className="fa fa-file-text-o me-2 text-success"></i>Monthly Pay Slip
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm text-start"
                                          style={{
                                            background: '#fff',
                                            border: '1px solid rgba(124,58,237,0.3)',
                                            color: '#6d28d9',
                                            borderRadius: '6px'
                                          }}
                                          onClick={() => openExpLetterModal(s)}
                                        >
                                          <i className="fa fa-certificate me-2 text-primary"></i>Experience Letter
                                        </button>
                                        <Link
                                          to={`/admin/attendance`}
                                          className="btn btn-sm text-start text-secondary border bg-white"
                                          style={{ borderRadius: '6px' }}
                                        >
                                          <i className="fa fa-calendar-check-o me-2"></i>Attendance & Payroll
                                        </Link>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="card text-center py-5"
                style={{ color: 'var(--text-muted)' }}
              >
                <i className="fa fa-id-card fa-3x mb-3" style={{ opacity: 0.3 }}></i>
                <p className="mb-0">No staff found.</p>
              </div>
            )}

            {/* DELETE CONFIRM MODAL */}
            <DeleteConfirmModal
              show={deleteModal.show}
              itemName={deleteModal.name}
              onClose={() => setDeleteModal({ show: false, id: null, name: '' })}
              onConfirm={confirmDelete}
            />

            {/* ========================================================================= */}
            {/* PAY SLIP GENERATOR MODAL */}
            {/* ========================================================================= */}
            {paySlipModal.show && paySlipModal.staff && (
              <div
                className="modal-backdrop-custom"
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  background: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(3px)',
                  zIndex: 1050,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1rem',
                  overflowY: 'auto',
                }}
              >
                <div
                  className="card shadow-lg border-0"
                  style={{
                    maxWidth: 620,
                    width: '100%',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    animation: 'modalSlideIn 0.2s ease-out',
                  }}
                >
                  {/* Header */}
                  <div
                    className="p-3 px-4 d-flex align-items-center justify-content-between text-white"
                    style={{ background: 'linear-gradient(135deg, #1b4d3e 0%, #2e7d32 100%)' }}
                  >
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="d-inline-flex align-items-center justify-content-center bg-white text-success rounded-circle"
                          style={{ width: 28, height: 28, fontSize: '0.9rem' }}
                        >
                          <i className="fa fa-file-text-o"></i>
                        </span>
                        <h5 className="modal-title mb-0 fw-bold text-white">Staff Salary Pay Slip</h5>
                      </div>
                      <small className="text-white-50 mt-1 d-block">
                        {paySlipModal.staff.name} ({paySlipModal.staff.staffId || 'Staff'}) • {paySlipModal.staff.designation || 'Staff Member'}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      aria-label="Close"
                      onClick={() => setPaySlipModal(prev => ({ ...prev, show: false }))}
                    ></button>
                  </div>

                  {/* Body */}
                  <div className="p-4" style={{ maxHeight: 'calc(85vh - 120px)', overflowY: 'auto' }}>
                    {/* Period selection */}
                    <div className="card bg-light border-0 mb-3 p-3 rounded-3">
                      <div className="row g-2 align-items-center">
                        <div className="col-6">
                          <label className="form-label small fw-semibold text-muted mb-1">Pay Month</label>
                          <select
                            className="form-select form-select-sm"
                            value={paySlipModal.month}
                            onChange={(e) => handlePaySlipMonthYearChange(paySlipModal.year, Number(e.target.value))}
                          >
                            {MONTH_NAMES.map((name, i) => (
                              <option key={i + 1} value={i + 1}>
                                {name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label small fw-semibold text-muted mb-1">Pay Year</label>
                          <select
                            className="form-select form-select-sm"
                            value={paySlipModal.year}
                            onChange={(e) => handlePaySlipMonthYearChange(Number(e.target.value), paySlipModal.month)}
                          >
                            {[-1, 0, 1].map(offset => {
                              const y = new Date().getFullYear() + offset;
                              return <option key={y} value={y}>{y}</option>;
                            })}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Attendance summary stats */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '0.5px' }}>
                          Attendance Breakdown (30-Day Base)
                        </span>
                        {paySlipModal.loading && (
                          <span className="spinner-border spinner-border-sm text-success" role="status"></span>
                        )}
                      </div>
                      <div className="row g-2 text-center">
                        <div className="col-3">
                          <div className="p-2 rounded border" style={{ background: 'rgba(16,185,129,0.06)', borderColor: 'rgba(16,185,129,0.2)' }}>
                            <div className="fw-bold text-success" style={{ fontSize: '1.1rem' }}>
                              {paySlipModal.summary.present || 0}
                            </div>
                            <small className="text-muted" style={{ fontSize: '0.72rem' }}>Present</small>
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="p-2 rounded border" style={{ background: 'rgba(59,130,246,0.06)', borderColor: 'rgba(59,130,246,0.2)' }}>
                            <div className="fw-bold text-primary" style={{ fontSize: '1.1rem' }}>
                              {paySlipModal.summary.holiday || 0}
                            </div>
                            <small className="text-muted" style={{ fontSize: '0.72rem' }}>Holidays / Off</small>
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="p-2 rounded border" style={{ background: 'rgba(245,158,11,0.06)', borderColor: 'rgba(245,158,11,0.2)' }}>
                            <div className="fw-bold text-warning" style={{ fontSize: '1.1rem' }}>
                              {paySlipModal.summary.halfDay || 0}
                            </div>
                            <small className="text-muted" style={{ fontSize: '0.72rem' }}>Half Days</small>
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="p-2 rounded border" style={{ background: 'rgba(239,68,68,0.06)', borderColor: 'rgba(239,68,68,0.2)' }}>
                            <div className="fw-bold text-danger" style={{ fontSize: '1.1rem' }}>
                              {paySlipModal.summary.absent || 0}
                            </div>
                            <small className="text-muted" style={{ fontSize: '0.72rem' }}>Leaves / Absent</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Salary & deductions form */}
                    <div className="row g-3">
                      <div className="col-6">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Basic Monthly Salary (₹)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={paySlipModal.baseSalary}
                          onChange={(e) => handleBaseSalaryChange(e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Special Allowances / Bonus (₹)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={paySlipModal.allowance}
                          onChange={(e) => setPaySlipModal(prev => ({ ...prev, allowance: e.target.value }))}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Leave / Absence Deduction (₹)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={paySlipModal.deduction}
                          onChange={(e) => setPaySlipModal(prev => ({ ...prev, deduction: e.target.value }))}
                        />
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                          Auto calculated: (Absent × 1 day) + (Half day × 0.5)
                        </small>
                      </div>
                      <div className="col-6">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Other Deductions / Tax / Advance (₹)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={paySlipModal.otherDeductions}
                          onChange={(e) => setPaySlipModal(prev => ({ ...prev, otherDeductions: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Net Payable Banner */}
                    {(() => {
                      const totalEarnings = Number(paySlipModal.baseSalary || 0) + Number(paySlipModal.allowance || 0);
                      const totalDeductions = Number(paySlipModal.deduction || 0) + Number(paySlipModal.otherDeductions || 0);
                      const netSalary = Math.max(0, totalEarnings - totalDeductions);
                      return (
                        <div
                          className="mt-4 p-3 rounded-3"
                          style={{
                            background: 'rgba(16,185,129,0.08)',
                            border: '1px solid rgba(16,185,129,0.25)',
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                              <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '0.72rem' }}>
                                Net Salary Payable
                              </small>
                              <div className="fw-bold text-success" style={{ fontSize: '1.4rem' }}>
                                ₹{netSalary.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            </div>
                            <div className="text-end">
                              <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
                                Paid Days: {paySlipModal.summary.payableDays || 30} / 30
                              </span>
                            </div>
                          </div>
                          <div className="text-muted small mt-1 font-monospace" style={{ fontSize: '0.78rem' }}>
                            {numberToWords(netSalary)}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Footer */}
                  <div className="p-3 px-4 bg-light border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                      onClick={() => setPaySlipModal(prev => ({ ...prev, show: false }))}
                    >
                      Close
                    </button>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-success btn-sm rounded-pill px-3 text-white fw-semibold shadow-sm"
                        style={{ background: '#25D366', borderColor: '#25D366' }}
                        onClick={handleSharePaySlipWhatsApp}
                        title="Send breakdown via WhatsApp"
                      >
                        <i className="fab fa-whatsapp me-1"></i>Share via WhatsApp
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm rounded-pill px-3 fw-semibold shadow-sm"
                        style={{ background: '#1b4d3e', borderColor: '#1b4d3e' }}
                        onClick={handleDownloadPaySlip}
                        title="Download A4 Salary Slip PDF"
                      >
                        <i className="fa fa-download me-1"></i>Download Pay Slip (PDF)
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================================================= */}
            {/* EXPERIENCE CERTIFICATE MODAL */}
            {/* ========================================================================= */}
            {expModal.show && expModal.staff && (
              <div
                className="modal-backdrop-custom"
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  background: 'rgba(15, 23, 42, 0.65)',
                  backdropFilter: 'blur(3px)',
                  zIndex: 1050,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1rem',
                  overflowY: 'auto',
                }}
              >
                <div
                  className="card shadow-lg border-0"
                  style={{
                    maxWidth: 620,
                    width: '100%',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    animation: 'modalSlideIn 0.2s ease-out',
                  }}
                >
                  {/* Header */}
                  <div
                    className="p-3 px-4 d-flex align-items-center justify-content-between text-white"
                    style={{ background: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)' }}
                  >
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="d-inline-flex align-items-center justify-content-center bg-white text-primary rounded-circle"
                          style={{ width: 28, height: 28, fontSize: '0.9rem' }}
                        >
                          <i className="fa fa-certificate"></i>
                        </span>
                        <h5 className="modal-title mb-0 fw-bold text-white">Experience Certificate / Letter</h5>
                      </div>
                      <small className="text-white-50 mt-1 d-block">
                        {expModal.staff.name} ({expModal.staff.staffId || 'Staff'})
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      aria-label="Close"
                      onClick={() => setExpModal(prev => ({ ...prev, show: false }))}
                    ></button>
                  </div>

                  {/* Body */}
                  <div className="p-4" style={{ maxHeight: 'calc(85vh - 120px)', overflowY: 'auto' }}>
                    <div className="row g-3">
                      <div className="col-6">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Reference Number
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={expModal.refNo}
                          onChange={(e) => setExpModal(prev => ({ ...prev, refNo: e.target.value }))}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Issue Date
                        </label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={expModal.issueDate}
                          onChange={(e) => setExpModal(prev => ({ ...prev, issueDate: e.target.value }))}
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Designation / Role Title
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={expModal.designation}
                          onChange={(e) => setExpModal(prev => ({ ...prev, designation: e.target.value }))}
                        />
                      </div>

                      <div className="col-6">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Date of Joining
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={expModal.joiningDate}
                          onChange={(e) => setExpModal(prev => ({ ...prev, joiningDate: e.target.value }))}
                        />
                      </div>

                      <div className="col-6">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Relieving Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          disabled={expModal.isCurrentlyWorking}
                          value={expModal.relievingDate}
                          onChange={(e) => setExpModal(prev => ({ ...prev, relievingDate: e.target.value }))}
                        />
                      </div>

                      <div className="col-12">
                        <div className="form-check p-2 ps-4 rounded bg-light border">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isCurrentlyWorkingCheck"
                            checked={expModal.isCurrentlyWorking}
                            onChange={(e) => setExpModal(prev => ({ ...prev, isCurrentlyWorking: e.target.checked }))}
                          />
                          <label className="form-check-label small fw-semibold text-dark" htmlFor="isCurrentlyWorkingCheck">
                            Currently working at Green Naturopathy (Tenure prints as <em>&quot;to till date&quot;</em>)
                          </label>
                        </div>
                      </div>

                      <div className="col-12">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Professional Conduct & Character Remarks
                        </label>
                        <textarea
                          className="form-control"
                          rows={4}
                          style={{ fontSize: '0.85rem' }}
                          value={expModal.remarks}
                          onChange={(e) => setExpModal(prev => ({ ...prev, remarks: e.target.value }))}
                        ></textarea>
                        <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                          This text will be included in the official experience certificate body.
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="p-3 px-4 bg-light border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                      onClick={() => setExpModal(prev => ({ ...prev, show: false }))}
                    >
                      Close
                    </button>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-success btn-sm rounded-pill px-3 text-white fw-semibold shadow-sm"
                        style={{ background: '#25D366', borderColor: '#25D366' }}
                        onClick={handleShareExpWhatsApp}
                        title="Send certificate notification via WhatsApp"
                      >
                        <i className="fab fa-whatsapp me-1"></i>Share via WhatsApp
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm rounded-pill px-3 fw-semibold text-white shadow-sm"
                        style={{ background: '#7c3aed', borderColor: '#7c3aed' }}
                        onClick={handleDownloadExpLetter}
                        title="Download A4 Experience Letter PDF"
                      >
                        <i className="fa fa-download me-1"></i>Download Certificate (PDF)
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
}
